// src/reporting/pdf/addDebtServiceChart.js

import dayjs from "lib/dayjs";
import { PDF_CONTENT_GAP, addSectionTitle, ensurePageSpace } from "./pdfLayout";
import { PDF_CHART_SERIES, PDF_STONE } from "./pdfTheme";

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function getPrincipal(row) {
  return num(row?.principal);
}

function getInterest(row) {
  return num(row?.interest);
}

function getExistingDebtService(row) {
  return num(
    row?.existingCashFlows ??
      row?.existingDebtService ??
      row?.existing_ds ??
      row?.existingDS
  );
}

function getPeriodTotal(row) {
  if (row?.periodTotal == null || row?.periodTotal === "") return null;

  const n = Number(row?.periodTotal);
  if (!Number.isFinite(n) || Math.abs(n) <= 0.005) return null;

  return n;
}

function formatKmb(value) {
  const n = Number(value) || 0;
  const a = Math.abs(n);

  if (a >= 1e9) return `${(n / 1e9).toFixed(a >= 10e9 ? 0 : 1)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 10e6 ? 0 : 1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(a >= 10e3 ? 0 : 1)}K`;

  return `${Math.round(n)}`;
}

function formatDateLabel(value) {
  const d = dayjs(value);
  if (!d.isValid()) return String(value || "");
  return d.format("M/YY");
}

function niceMax(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;

  const exponent = Math.floor(Math.log10(n));
  const magnitude = 10 ** exponent;
  const normalized = n / magnitude;

  let nice = 10;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;

  return nice * magnitude;
}

function buildTicks(maxValue, tickCount = 5) {
  const max = niceMax(maxValue);
  const step = max / Math.max(1, tickCount - 1);

  return Array.from({ length: tickCount }, (_, index) => step * index);
}

function drawLegendItem(doc, x, y, color, label, options = {}) {
  const { line = false } = options;

  doc.setDrawColor(...color);
  doc.setFillColor(...color);

  if (line) {
    doc.setLineWidth(1.4);
    doc.line(x, y - 3, x + 14, y - 3);
    doc.circle(x + 7, y - 3, 1.5, "F");
  } else {
    doc.rect(x, y - 8, 12, 7, "F");
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(70);
  doc.text(label, x + 18, y - 2);
}

function drawLineSeries(doc, points, color) {
  const drawable = points.filter((point) => point.value != null);
  if (drawable.length < 2) return;

  doc.setDrawColor(...color);
  doc.setLineWidth(1.4);

  for (let i = 1; i < drawable.length; i += 1) {
    doc.line(drawable[i - 1].x, drawable[i - 1].y, drawable[i].x, drawable[i].y);
  }

  doc.setFillColor(...color);
  drawable.forEach((point) => {
    doc.circle(point.x, point.y, 1.7, "F");
  });
}

export function addDebtServiceChart(ctx, config = {}, startY) {
  const { doc, margins, usableWidth } = ctx;
  const { title = "Debt Service Chart", rows = [] } = config;

  const data = (Array.isArray(rows) ? rows : [])
    .filter((row) => {
      return (
        getPrincipal(row) !== 0 ||
        getInterest(row) !== 0 ||
        getExistingDebtService(row) !== 0 ||
        getPeriodTotal(row) != null
      );
    })
    .map((row) => ({
      date: row?.date || row?.paymentDate || row?.payment_date || "",
      principal: getPrincipal(row),
      interest: getInterest(row),
      existingDebtService: getExistingDebtService(row),
      periodTotal: getPeriodTotal(row),
    }));

  const chartHeight = 230;
  const neededHeight = chartHeight + 42;
  let y = ensurePageSpace(ctx, startY, neededHeight);

  y = addSectionTitle(ctx, title, y);

  if (!data.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text("No chart data available.", margins.left, y);
    return y + PDF_CONTENT_GAP;
  }

  const chartX = margins.left;
  const chartY = y;
  const chartW = usableWidth;
  const plotLeft = chartX + 42;
  const plotRight = chartX + chartW - 10;
  const plotTop = chartY + 8;
  const plotBottom = chartY + chartHeight - 44;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const maxValue = Math.max(
    ...data.map((row) => row.principal + row.interest),
    ...data.map((row) => row.existingDebtService),
    ...data.map((row) => row.periodTotal || 0),
    1
  );
  const ticks = buildTicks(maxValue, 5);
  const yMax = ticks[ticks.length - 1] || 1;
  const yPos = (value) => plotBottom - (Math.max(0, value) / yMax) * plotH;
  const barSlot = data.length > 0 ? plotW / data.length : plotW;
  const barW = Math.max(1.8, Math.min(14, barSlot * 0.56));
  const xPos = (index) => plotLeft + barSlot * index + barSlot / 2;

  const colors = {
    principal: PDF_CHART_SERIES[0],
    interest: PDF_CHART_SERIES[1],
    periodTotal: PDF_CHART_SERIES[2],
    existing: PDF_CHART_SERIES[3],
    grid: PDF_STONE.grid,
    axis: PDF_STONE.axis,
  };

  doc.setDrawColor(215);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(chartX, chartY, chartW, chartHeight, 5, 5, "S");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);

  ticks.forEach((tick) => {
    const yy = yPos(tick);

    doc.setDrawColor(...colors.grid);
    doc.setLineWidth(0.35);
    doc.line(plotLeft, yy, plotRight, yy);

    doc.setTextColor(85);
    doc.text(`$${formatKmb(tick)}`, plotLeft - 7, yy + 2.5, {
      align: "right",
    });
  });

  doc.setDrawColor(...colors.axis);
  doc.setLineWidth(0.6);
  doc.line(plotLeft, plotBottom, plotRight, plotBottom);
  doc.line(plotLeft, plotTop, plotLeft, plotBottom);

  data.forEach((row, index) => {
    const x = xPos(index);
    const barX = x - barW / 2;
    const principalTop = yPos(row.principal);
    const interestTop = yPos(row.principal + row.interest);

    if (row.principal > 0) {
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.25);
      doc.setFillColor(...colors.principal);
      doc.rect(barX, principalTop, barW, plotBottom - principalTop, "FD");
    }

    if (row.interest > 0) {
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.25);
      doc.setFillColor(...colors.interest);
      doc.rect(barX, interestTop, barW, principalTop - interestTop, "FD");
    }
  });

  const periodPoints = data.map((row, index) => {
    const value = row.periodTotal;
    const x = xPos(index);

    return {
      x,
      y: value == null ? null : yPos(value),
      value,
    };
  });

  const existingPoints = data.map((row, index) => {
    const value =
      Math.abs(row.existingDebtService) > 0.005 ? row.existingDebtService : null;
    const x = xPos(index);

    return {
      x,
      y: value == null ? null : yPos(value),
      value,
    };
  });

  drawLineSeries(doc, periodPoints, colors.periodTotal);
  drawLineSeries(doc, existingPoints, colors.existing);

  const labelEvery = Math.max(1, Math.ceil(data.length / 8));
  data.forEach((row, index) => {
    if (index % labelEvery !== 0 && index !== data.length - 1) return;

    const x = xPos(index);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(85);
    doc.text(formatDateLabel(row.date), x, plotBottom + 12, {
      align: "center",
    });
  });

  const legendY = chartY + chartHeight - 14;
  let legendX = plotLeft;

  drawLegendItem(doc, legendX, legendY, colors.principal, "Principal");
  legendX += 78;
  drawLegendItem(doc, legendX, legendY, colors.interest, "Interest");
  legendX += 70;
  drawLegendItem(doc, legendX, legendY, colors.periodTotal, "Period Total", {
    line: true,
  });

  if (existingPoints.some((point) => point.value != null)) {
    legendX += 88;
    drawLegendItem(doc, legendX, legendY, colors.existing, "Existing D/S", {
      line: true,
    });
  }

  return chartY + chartHeight + PDF_CONTENT_GAP;
}

export default addDebtServiceChart;
